import { useState, useEffect, useCallback } from "react";
import { getCurrentUser } from "../../../../store/instanceManagement/instanceService";
import { logger } from "../../../../logger-init";

interface CurrentUser {
  fullname: string;
  firstname?: string;
  lastname?: string;
  email: string;
  isPlatformAdmin: boolean;
  lastLogin?: string;
  [key: string]: unknown;
}

interface UseCurrentUserReturn {
  currentUser: CurrentUser | null;
  initials: string;
  isLoaded: boolean;
  refresh: () => void;
}

function getInitials(fullname: string): string {
  if (!fullname) return "";
  const parts = fullname.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// One request per page, not one per mount. The header, profile menu, instance
// switcher and For You page all mount this hook on boot; each used to fetch
// /api/user/loggedinUser on its own (four auth validations + four user
// lookups for one answer). The value and the in-flight promise live at module
// scope, and every mounted consumer is told when either changes.
let cachedUser: CurrentUser | null = null;
let hasLoaded = false;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function load(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = getCurrentUser()
    .then((data: CurrentUser) => {
      cachedUser = data;
    })
    .catch((error) => {
      logger.error("Failed to get current user", error instanceof Error ? error : undefined);
    })
    .finally(() => {
      hasLoaded = true;
      inFlight = null;
      notify();
    });
  return inFlight;
}

/** Test seam: forget the shared value so each test starts cold. */
export function __resetCurrentUserCacheForTests(): void {
  cachedUser = null;
  hasLoaded = false;
  inFlight = null;
  listeners.clear();
}

export function useCurrentUser(): UseCurrentUserReturn {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(cachedUser);
  const [isLoaded, setIsLoaded] = useState<boolean>(hasLoaded);

  useEffect(() => {
    const sync = () => {
      setCurrentUser(cachedUser);
      setIsLoaded(hasLoaded);
    };
    listeners.add(sync);
    if (hasLoaded) sync();
    else void load();
    return () => {
      listeners.delete(sync);
    };
  }, []);

  const refresh = useCallback(() => {
    hasLoaded = false;
    notify();
    void load();
  }, []);

  return {
    currentUser,
    initials: getInitials(currentUser?.fullname || ""),
    isLoaded,
    refresh,
  };
}
